import React from "react";
import { Button, Grid, TextField } from "@mui/material";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import AppForm from "components/Shared/AppForm";
import AppSwitch from "components/Shared/AppSwitch";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import BaseSelect from "components/Shared/Base/BaseSelect";

import { useGetDDLAccountListQuery } from "store/services/accountApi";
import { useLazyGetDDLEntityListQuery } from "store/services/entityApi";

import { CROSS_DOCKING_STATUS } from "data/static/staticOptions";

import { useAuth } from "hooks";
import { useLazyGetDDLShipmentListQuery } from "store/services/shipmentApi";
import { useLazyGetDDLBranchListQuery } from "store/services/branchApi";

const INITIAL_STATE = {
  account: "",
  entity: "",
  shipmentId: "",
  sourceBranch: "",
  destinationBranch: "",
  transferTime: "",
  status: "",
  isActive: "",
};
const CrossDockingForm = ({
  isBusy = null,
  crossDockingDetail = null,
  onSubmit = () => {},
}) => {
  const navigate = useNavigate();

  const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();

  // react hook form
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      ...INITIAL_STATE,
      entity: viewEntity?.entity,
      account: viewEntity?.entity?.account,
    },
  });

  const _watchEntity = useWatch({ control, name: "entity" });
  const _watchAccount = useWatch({ control, name: "account" });

  // RTK Query
  const { data: accounts, isLoading: loadingAccounts } =
    useGetDDLAccountListQuery();

  // RTK Query
  const [fetchEntities, { data: entities, isLoading: loadingEntities }] =
    useLazyGetDDLEntityListQuery();
  const [fetchShipments, { data: shipments, isLoading: loadingShipments }] =
    useLazyGetDDLShipmentListQuery();

  const [fetchBranches, { data: branches, isLoading: loadingBranches }] =
    useLazyGetDDLBranchListQuery();

  // methods
  const handleOnCrossDockingSubmit = (data) => {
    const _crossDockingPayload = {
      ...data,
      entity: data?.entity?._id,
      account: data?.account?._id,
      shipmentId: data?.shipmentId?._id,
      sourceWarehouseId: data?.sourceWarehouseId?._id,
      destinationWarehouseId: data?.destinationWarehouseId?._id,
    };
    onSubmit(_crossDockingPayload);
  };
  React.useEffect(() => {
    if (!crossDockingDetail) return;
    reset({
      ...crossDockingDetail,
      transferTime: crossDockingDetail?.transferTime
        ? new Date(crossDockingDetail.transferTime).toISOString().slice(0, 16)
        : "",
    });
  }, [crossDockingDetail, reset]);

  React.useEffect(() => {
    if (isAccountAdmin || isSystemAdmin)
      fetchEntities({
        account: _watchAccount?._id ?? viewEntity?.entity?.account?._id?._id,
      });
  }, [
    _watchAccount?._id,
    fetchEntities,
    isAccountAdmin,
    isSystemAdmin,
    viewEntity?.entity?.account,
  ]);

  React.useEffect(() => {
    if (!_watchEntity?._id) return;
    fetchShipments({ account: _watchAccount?._id, entity: _watchEntity?._id });
  }, [_watchAccount?._id, _watchEntity?._id, fetchShipments, viewEntity]);
  React.useEffect(() => {
    if (!_watchEntity?._id) return;
    fetchBranches({ account: _watchAccount?._id, entity: _watchEntity?._id });
  }, [_watchAccount?._id, _watchEntity?._id, fetchBranches, viewEntity]);

  return (
    <AppForm loading={isBusy}>
      <AppForm.Content>
        <Grid container spacing={4}>
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="account"
                control={control}
                rules={{ required: "Please select an account." }}
                render={({ field }) => (
                  <BaseAutocomplete
                    {...field}
                    fullWidth
                    label="Account"
                    required
                    value={field.value ?? ""}
                    onChange={(_, data) => {
                      field.onChange(data);
                      setValue("entity", "");
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={accounts || []}
                    loading={loadingAccounts}
                    loadingText="Loading Account..."
                    disabled={isAccountAdmin}
                    helperText={errors?.account?.message}
                    error={!!errors?.account}
                  />
                )}
              />
            </Grid>
          )}
          {(isAccountAdmin || isSystemAdmin) && (
            <Grid item md={6}>
              <Controller
                name="entity"
                control={control}
                rules={{ required: "Please select an entity." }}
                render={({ field: { value, onChange } }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Entity"
                    value={value ?? ""}
                    onChange={(_, data) => {
                      onChange(data);
                      setValue("vehicleId", "");
                    }}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={entities || []}
                    loading={loadingEntities}
                    loadingText="Loading Entities..."
                    helperText={errors?.entity?.message}
                    error={!!errors?.entity}
                  />
                )}
              />
            </Grid>
          )}

          <Grid item md={6}>
            <Controller
              name="shipmentId"
              control={control}
              rules={{ required: "Please select shipment ID." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Shipment ID"
                  required
                  value={value ?? ""}
                  onChange={(_, data) => {
                    onChange(data);
                    setValue("sourceBranch", data?.sourceBranch, {
                      shouldValidate: true,
                    });
                  }}
                  getOptionLabel={(option) => option?.shipmentId ?? ""}
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={shipments || []}
                  loading={loadingShipments}
                  loadingText="Loading Shipment ID..."
                  helperText={errors?.shipmentId?.message}
                  error={!!errors?.shipmentId}
                  disabled={!!crossDockingDetail}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <Controller
              name="sourceBranch"
              control={control}
              rules={{ required: "Please select source branch." }}
              render={({ field: { value, onChange } }) => (
                <BaseAutocomplete
                  fullWidth
                  label="Source Branch"
                  required
                  value={value ?? ""}
                  onChange={(_, data) => {
                    onChange(data);
                  }}
                  getOptionLabel={(option) =>
                    option && `${option?.name} - ${option?.code}`
                  }
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id
                  }
                  options={branches || []}
                  loading={loadingBranches}
                  loadingText="Loading source branches..."
                  helperText={errors?.sourceBranch?.message}
                  error={!!errors?.sourceBranch}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="destinationBranch"
              control={control}
              rules={{ required: "Please select destination branch." }}
              render={({ field: { value, onChange } }) => {
                return (
                  <BaseAutocomplete
                    fullWidth
                    label="Destination Branch"
                    required
                    value={value ?? ""}
                    onChange={(_, data) => {
                      onChange(data);
                    }}
                    getOptionLabel={(option) =>
                      option && `${option?.name} - ${option?.code}`
                    }
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    options={branches || []}
                    loading={loadingBranches}
                    loadingText="Loading destination branches..."
                    helperText={errors?.destinationBranch?.message}
                    error={!!errors?.destinationBranch}
                  />
                );
              }}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="transferTime"
              control={control}
              rules={{ required: "Transfer Time is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Transfer Time"
                  type="datetime-local"
                  fullWidth
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.transferTime}
                  helperText={errors?.transferTime?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Controller
              name="status"
              control={control}
              rules={{ required: "Add status" }}
              render={({ field }) => (
                <BaseSelect
                  {...field}
                  required
                  fullWidth
                  size="small"
                  label="Status"
                  options={CROSS_DOCKING_STATUS}
                />
              )}
            />
          </Grid>

          {crossDockingDetail && (
            <Grid item xs={6}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <AppSwitch {...field} label="Is Active?" />
                )}
              />
            </Grid>
          )}
        </Grid>
      </AppForm.Content>
      <AppForm.Actions>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit(handleOnCrossDockingSubmit)}
        >
          {crossDockingDetail ? "Update" : "Add"}
        </Button>
      </AppForm.Actions>
    </AppForm>
  );
};

export default CrossDockingForm;
